import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl } from '..';
import { faqsInitialValues as initialValues, faqsSchema as validationSchema } from '.';
import axios from 'axios';
import { NETLIFY_FUNCTIONS_PATH, getFacebookCookies, validateWithPossibleNow } from '../../../utils';
import { navigate } from 'gatsby';
import { FormSuccess, FormFailed, FormSubmitting, AttributionSurvey, ConsentMsg } from '../shared';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';

export function FaqsForm({ siteData }) {
    initialValues.location = siteData.currentMetro.stores[0].salesforceValue;
    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('ask_new_question');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                updates: true,
                cloudamp__data__c: localStorage.getItem('cloudamp__data__c'),
                formType: 'faqs',
                fbp,
                fbc,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            // Store form values for use in attribution survey
            setFormValues(formData);

            await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

            // Show attribution survey instead of immediate success
            setShowAttributionSurvey(true);

            helpers.setSubmitting(false);
            helpers.setStatus('Success');

            navigate('?faqs-form-success#faqs-form'); // This is for Marketing

            helpers.resetForm();
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <section id="ask_new_question" className="w-100 py-5">
            <div className="container ">
                <div className="row milan-text-white align-items-center">
                    <div className="col-lg-4 text-center mb-3">
                        <h5 className="text-capitalize">Don’t see your question answered?</h5>
                        <h3 className="subpointlg">Ask it here</h3>
                    </div>
                    <div className="col-lg-8">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            validateOnChange={false}
                        >
                            {(formik) => {
                                if (formik.isSubmitting) {
                                    return <FormSubmitting />;
                                }

                                // Handle error state
                                if (formik.status === 'Failed') {
                                    return <FormFailed />;
                                }

                                if (formValues) {
                                    // At this point the form is submitted
                                    if (showAttributionSurvey) {
                                        return (
                                            <AttributionSurvey
                                                formValues={formValues}
                                                onComplete={handleAttributionComplete}
                                            />
                                        );
                                    }
                                    return <FormSuccess />;
                                }
                                return (
                                    <Form className="w-100 pb-4">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-2">
                                                    <FormikControl
                                                        control="textarea"
                                                        name="description"
                                                        placeholder="Your Question"
                                                        rows={siteData.currentMetro.stores.length > 1 ? '7' : '5'}
                                                        role="input"
                                                        aria-required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="row gx-2">
                                                    <div className="col-md-6">
                                                        <div className="mb-2">
                                                            <FormikControl
                                                                control="input"
                                                                name="first_name"
                                                                placeholder="First Name"
                                                                type="text"
                                                                role="input"
                                                                aria-required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-2">
                                                            <FormikControl
                                                                control="input"
                                                                name="last_name"
                                                                placeholder="Last Name"
                                                                type="text"
                                                                role="input"
                                                                aria-required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <FormikControl
                                                        control="phoneInput"
                                                        name="phone"
                                                        placeholder="Phone Number"
                                                        type="tel"
                                                        role="input"
                                                        aria-required
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <FormikControl
                                                        control="emailInput"
                                                        name="email"
                                                        placeholder="Your Email"
                                                        role="input"
                                                        aria-required
                                                    />
                                                </div>
                                                {siteData.currentMetro.stores.length > 1 && (
                                                    <div>
                                                        <FormikControl
                                                            control="selectLocation"
                                                            name="location"
                                                            milanStores={siteData.milanStores}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="row justify-content-center mb-3">
                                            <div className="col-12 lh-1">
                                                <ConsentMsg cta="SEND QUESTION<sup>*</sup>" />
                                            </div>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-12 text-center">
                                                <button
                                                    className="milan-btn milan-red-btn w-100 send-question-btn"
                                                    type="submit"
                                                    disabled={!formik.isValid}
                                                    style={{
                                                        maxWidth: '300px',
                                                    }}
                                                >
                                                    {formik.isSubmitting ? (
                                                        'Sending...'
                                                    ) : (
                                                        <>
                                                            SEND QUESTION <sup>*</sup>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default FaqsForm;
